import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useAuthority } from '@/hooks/useAuthority'
import { SpecialtiesService, type Specialty } from '@/services/modules/SpecialtiesService'
import { toast } from '@/components/ui/Toast'

export function WorkflowSpecialtiesPage() {
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar especialidade' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(SpecialtiesService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: Specialty }>({ open: false })
  const [openDelete, setOpenDelete] = useState<Specialty | null>(null)
  const { can } = useAuthority()
  const canCreate = can('workflow.specialties.create')
  const canEdit = can('workflow.specialties.edit')
  const canDelete = can('workflow.specialties.delete')

  const exp = useDataTableExport({
    filename: 'especialidades',
    service: SpecialtiesService,
    filters: sf.filters,
    columns: [
      { header: 'Especialidade', accessor: (s: Specialty) => s.specialty },
      { header: 'Código TUSS',   accessor: (s: Specialty) => s.code ?? '' },
      { header: 'Status',        accessor: (s: Specialty) => s.status },
    ],
  })

  async function handleSave(payload: Partial<Specialty>) {
    try {
      if (openForm.item) { await SpecialtiesService.update(openForm.item.id, payload); toast.success('Especialidade atualizada.') }
      else { await SpecialtiesService.create(payload); toast.success('Especialidade criada.') }
      setOpenForm({ open: false }); setReload(v => v + 1)
    } catch (e) { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.') }
  }
  async function handleDelete() {
    if (!openDelete) return
    try { await SpecialtiesService.destroy(openDelete.id); toast.success('Especialidade removida.'); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error('Erro ao remover.') }
  }

  const columns: DataTableColumn<Specialty>[] = [
    { key: 'specialty', header: 'Especialidade', render: s => <span className="font-medium text-gray-800">{s.specialty}</span>, sortable: true, alwaysVisible: true },
    { key: 'code',      header: 'TUSS',          align: 'center', render: s => <span className="text-xs text-gray-500">{s.code ?? '—'}</span>, className: 'w-40' },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: s => (
        <StatusDot value={s.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader title="Especialidades" subtitle="Catálogo de especialidades médicas (TUSS)"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Workflow' }, { label: 'Especialidades' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Nova especialidade</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="workflow.specialties"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(s) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />,  onClick: () => setOpenForm({ open: true, item: s }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />,  danger: true, divider: canEdit, onClick: () => setOpenDelete(s) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <SpecialtyForm open={openForm.open} item={openForm.item} onClose={() => setOpenForm({ open: false })} onSave={handleSave} />
      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover especialidade?"
        description={`Tem certeza que deseja remover "${openDelete?.specialty}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}

function SpecialtyForm({ open, item, onClose, onSave }: { open: boolean; item?: Specialty; onClose: () => void; onSave: (payload: Partial<Specialty>) => Promise<void> }) {
  const [specialty, setSpecialty] = useState('')
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (open) { setSpecialty(item?.specialty ?? ''); setCode(item?.code ?? ''); setStatus(item?.status ?? 'ATIVO') }
  }, [open, item])

  async function submit() { setSaving(true); await onSave({ specialty, code: code || null, status }); setSaving(false) }
  return (
    <Modal open={open} onClose={onClose}
      title={item ? 'Editar especialidade' : 'Nova especialidade'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={submit} loading={saving} disabled={!specialty.trim()}>Salvar</Button></>}
    >
      <div className="space-y-3">
        <Input label="Especialidade" required value={specialty} onChange={e => setSpecialty(e.target.value)} placeholder="Cardiologia" />
        <Input label="Código TUSS"   value={code}      onChange={e => setCode(e.target.value)}      placeholder="2.01.01.04-1" />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}
